'use client';

// ─────────────────────────────────────────────────────────────────────────────
// MergedEscalationsPanel — single unified list of escalations routed to the
// PM/QC merged role. Replaces the two separate "PM Escalations" and "QC
// Escalations" panels that used to live on DashboardInteractive and inside
// QCInspectionPanel. Each row carries a small PM/QC source tag so the user
// can tell where it originated; the merged role acts on both anyway.
//
// Fetches `escalations` filtered by `source_department IN
// ('Project_Manager', 'Quality_Control')`, status != 'closed'. No realtime
// subscription — refresh button + tab remount on first load are enough for
// this lower-frequency surface.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

type Source = 'Project_Manager' | 'Quality_Control';

interface EscalationRow {
  id: string;
  ticket_id: string;
  title: string;
  priority: string | null;
  status: string;
  source_department: Source;
  affected_job_id: string | null;
  created_at: string;
  response_due: string | null;
  resolution_due: string | null;
}

const SOURCE_LABEL: Record<Source, string> = {
  Project_Manager: 'PM',
  Quality_Control: 'QC',
};

interface MergedEscalationsPanelProps {
  onOpenEscalationModal?: () => void;
}

export default function MergedEscalationsPanel({ onOpenEscalationModal }: MergedEscalationsPanelProps) {
  const [rows, setRows] = useState<EscalationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEscalations = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('escalations')
        .select('id, ticket_id, title, priority, status, source_department, affected_job_id, created_at, response_due, resolution_due')
        .in('source_department', ['Project_Manager', 'Quality_Control'])
        .neq('status', 'closed')
        .order('created_at', { ascending: false })
        .limit(20);
      setRows((data ?? []) as EscalationRow[]);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEscalations();
  }, [fetchEscalations]);

  const openCount = rows.filter(e => e.status !== 'resolved').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FEF2F2' }}>
            <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#EF4444' }} />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ color: '#000150' }}>Escalations</h2>
            <p className="text-xs" style={{ color: '#6B7280' }}>Tickets routed to Project Manager / Quality Control</p>
          </div>
          <span
            className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ backgroundColor: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA' }}
          >
            {openCount} OPEN
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchEscalations}
            className="flex items-center gap-1.5 px-4 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
            style={{ minHeight: '44px' }}
          >
            <Icon name="ArrowPathIcon" size={13} />
            Refresh
          </button>
          {onOpenEscalationModal && (
            <button
              onClick={onOpenEscalationModal}
              className="flex items-center gap-1 px-3 text-xs font-medium hover:underline"
              style={{ color: '#07B0E9', minHeight: '44px' }}
            >
              View All Escalations
              <Icon name="ArrowRightIcon" size={13} />
            </button>
          )}
        </div>
      </div>

      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
      >
        {loading ? (
          <div className="p-8 flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-gray-200 border-t-cyan-500 rounded-full animate-spin" />
            <span className="text-sm text-gray-400">Loading escalations…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: '#DCFCE7' }}>
              <Icon name="CheckCircleIcon" size={24} style={{ color: '#10B981' }} />
            </div>
            <p className="text-sm font-semibold text-gray-600">No active escalations</p>
            <p className="text-xs text-gray-400 mt-1">Everything routed to PM / QC is resolved or closed</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {rows.map(ticket => {
              const isHigh = ticket.priority === 'high' || ticket.priority === 'critical';
              const isMedium = ticket.priority === 'medium';
              const severityColor = isHigh ? '#EF4444' : isMedium ? '#F59E0B' : '#10B981';
              const severityBg = isHigh ? '#FEF2F2' : isMedium ? '#FFFBEB' : '#F0FDF4';
              const priorityBorderColor = isHigh ? '#FECACA' : isMedium ? '#FDE68A' : '#BBF7D0';
              const priorityTextColor = isHigh ? '#EF4444' : isMedium ? '#D97706' : '#16A34A';
              const statusColors: Record<string, { bg: string; text: string; border: string }> = {
                open: { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
                in_progress: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
                resolved: { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' },
              };
              const sc = statusColors[ticket.status] || { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' };
              const isResponseBreached = ticket.response_due ? new Date(ticket.response_due).getTime() < Date.now() : false;
              const isResolutionBreached = ticket.resolution_due ? new Date(ticket.resolution_due).getTime() < Date.now() : false;
              const sourceTag = SOURCE_LABEL[ticket.source_department] ?? '—';
              const sourceBg = ticket.source_department === 'Quality_Control' ? 'rgba(16,185,129,0.12)' : 'rgba(7,176,233,0.12)';
              const sourceColor = ticket.source_department === 'Quality_Control' ? '#059669' : '#0E7CB7';

              const timeAgoStr = (() => {
                const diff = Date.now() - new Date(ticket.created_at).getTime();
                const mins = Math.floor(diff / 60000);
                if (mins < 60) return `${mins}m ago`;
                const hrs = Math.floor(mins / 60);
                if (hrs < 24) return `${hrs}h ago`;
                return `${Math.floor(hrs / 24)}d ago`;
              })();

              return (
                <div key={ticket.id} className="flex items-start gap-3 px-4 sm:px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: severityBg, border: `1.5px solid ${priorityBorderColor}` }}>
                      <Icon name="ExclamationTriangleIcon" size={16} style={{ color: severityColor }} />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs font-bold" style={{ color: '#EC4899' }}>{ticket.ticket_id}</span>
                      <span
                        className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: sourceBg, color: sourceColor }}
                        title={`Source: ${ticket.source_department.replace('_', ' ')}`}
                      >
                        {sourceTag}
                      </span>
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                        style={{ backgroundColor: severityBg, color: priorityTextColor, borderColor: priorityBorderColor }}
                      >
                        {(ticket.priority || 'low').toUpperCase()}
                      </span>
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                        style={{ backgroundColor: sc.bg, color: sc.text, borderColor: sc.border }}
                      >
                        {ticket.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 truncate">{ticket.title}</p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {ticket.affected_job_id && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Icon name="BriefcaseIcon" size={11} />
                          {ticket.affected_job_id}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Icon name="ClockIcon" size={11} />
                        {timeAgoStr}
                      </span>
                      {ticket.response_due && (
                        <span className={`text-xs flex items-center gap-1 font-medium ${isResponseBreached ? 'text-red-500' : 'text-gray-400'}`}>
                          {isResponseBreached && <Icon name="ExclamationCircleIcon" size={11} />}
                          Response: {new Date(ticket.response_due).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                          {isResponseBreached && ' ⚠'}
                        </span>
                      )}
                      {ticket.resolution_due && (
                        <span className={`text-xs flex items-center gap-1 font-medium ${isResolutionBreached ? 'text-red-500' : 'text-gray-400'}`}>
                          {isResolutionBreached && <Icon name="ExclamationCircleIcon" size={11} />}
                          Resolve by: {new Date(ticket.resolution_due).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                          {isResolutionBreached && ' ⚠'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <a
                      href={`/escalation-management-dashboard?ticket=${ticket.ticket_id}`}
                      className="flex items-center gap-1.5 px-4 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90 whitespace-nowrap"
                      style={{ backgroundColor: '#07B0E9', boxShadow: '0 2px 6px rgba(7,176,233,0.3)', minHeight: '44px' }}
                    >
                      <Icon name="ArrowTopRightOnSquareIcon" size={13} />
                      View Ticket
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
